import type {
  Parameter,
  PayHubCashierContractsInitParamsResponse,
  PayHubCashierContractsPaymentGroupInfo as GroupInfo,
  PayHubCashierContractsPayoutsInitPayoutGroupsResponse as InitSuccess,
  PayHubWidgetContractsPayoutsSelectPayoutGroupResponse,
  PayoutGroupCategories,
  PayoutProcessMethodInfo,
} from 'store/models';
import {
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamType,
  PayHubAdminPanelDomainEnumsParameterDataType,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType as ActionTypes,
} from 'store/models';
import {
  DISABLED_CATEGORY_KEY,
  filterMethodsByPayType,
  getInitialTheme,
  getViewData,
  parseQuery,
  stringToInt,
  transformContentUrl,
} from 'utils';
import { isMethodAvailable } from 'utils/getIsMethodActive';
import { isPwContext } from 'utils/pwContext';
import { getSelectedCurrency, mapToCurrencyBase } from 'utils/wallet';

import { DEFAULT_EMPTY_CURRENCY_ALPHA3, DEFAULT_EMPTY_CURRENCY_PRECISION, noMethodsError } from '../constants';
import type { GeneralInfo, UIPaymentGroups, UIPayout, UIPayoutGroups, ViewDataType } from './commonPayments';
import {
  formNotificationData,
  getCarouselMethodsAndMethodToRedirect,
  getCurrency,
  getEnabled,
  getIsLimitReached,
  getMaxAmount,
  getMethodsTypes,
  getMinLimit,
  sortByArrangement,
} from './commonPayments';

const WAGERING_BRANDS = ['brand22', 'brand40', 'brand239', 'brand250', 'brand75'];

const formWithdrawalMethod =
  (generalInfo: GeneralInfo, paygroupCategories?: PayoutGroupCategories) =>
  (pmMethod: GroupInfo): UIPayoutGroups => {
    const { isLimitReached, generalViewData, disabledByWager } = generalInfo;
    const { display_limits = false } = pmMethod;
    const methodId = pmMethod.id || 0;
    const currentCurrency = getCurrency(pmMethod);
    const min = getMinLimit(currentCurrency);
    const max = getMaxAmount(currentCurrency);
    const infoLink = pmMethod.info;
    const precisionValue = currentCurrency.currency?.precision;
    const currencyPrecision =
      precisionValue != null && Number(precisionValue) <= DEFAULT_EMPTY_CURRENCY_PRECISION
        ? Number(precisionValue)
        : DEFAULT_EMPTY_CURRENCY_PRECISION;
    const isIncludedInDisabledCategory = Boolean(
      paygroupCategories?.categories
        .find((c) => c.category_name === DISABLED_CATEGORY_KEY)
        ?.pay_group_ids?.includes(methodId),
    );

    return {
      arrangement: pmMethod.arrangement || 0,
      id: pmMethod.id || 0,
      name: pmMethod.short_name?.trim() || '',
      displayName: pmMethod.public_name || '',
      isPopular: Boolean(pmMethod.popular),
      logoUri:
        pmMethod.logo && pmMethod.logo.startsWith('/') && isPwContext() ? `/pw${pmMethod.logo}` : pmMethod.logo || '',
      nativePayType: pmMethod.native_pay_type || '',
      paymentType: '',
      requiredFields: [],
      currencyId: currentCurrency.currency?.currency_id || 0,
      currencyAlpha3: currentCurrency.currency?.alpha3 || '',
      currencyPrecision,
      descriptionUrl: infoLink ? transformContentUrl(infoLink) : '',
      limits: {
        min,
        max,
        displayLimits: display_limits,
      },
      amountRegExpFe: '',
      viewData: [],
      payGroupActionType: pmMethod.action_type || '',
      merchantUserFee: pmMethod.merchant_user_fee,
      fee: currentCurrency.fee || '',
      processingTimeTranslationKey: currentCurrency.processing_time_translation_key || undefined,
      showProcessingTime: currentCurrency.show_processing_time || false,
      isSelfTarget: pmMethod.info_in_iframe || false,
      enabled: getEnabled(isLimitReached, min, pmMethod, generalViewData, disabledByWager),
      disabledByCategory: isIncludedInDisabledCategory,
      restrictedUntil: pmMethod?.restricted_until,
      pay_method_type: pmMethod.pay_method_type || '',
      last_used: pmMethod.last_used || false,
      autoSubmit: pmMethod.auto_submit || false,
      isCheckout: false,
      isLoading: true,
      showInCarousel: pmMethod.show_in_carousel || false,
      walletConnect: pmMethod.wallet_connect || false,
    };
  };

export const getMethodForAutoRedirectWithdrawal = (props: {
  methods: UIPayoutGroups[];
  initCarouselMethods?: UIPayoutGroups[];
  isInitialCarouselLoad?: boolean;
}): UIPayoutGroups | undefined => {
  const { methods = [], initCarouselMethods, isInitialCarouselLoad } = props;
  const [firstMethod] = methods;

  if (!firstMethod) {
    return undefined;
  }

  if (
    methods.length === 1 &&
    isMethodAvailable(firstMethod) &&
    firstMethod.payGroupActionType !== ActionTypes.NestedList
  ) {
    return firstMethod;
  }

  const { pay_group = '' } = parseQuery(window.location.search);
  const methodFromQuery = methods.find(({ id, name }) => id === stringToInt(pay_group as string) || name === pay_group);

  if (
    methodFromQuery &&
    isMethodAvailable(methodFromQuery) &&
    methodFromQuery.payGroupActionType !== ActionTypes.NestedList
  ) {
    return methodFromQuery;
  }

  if (
    initCarouselMethods &&
    initCarouselMethods.length > 1 &&
    !isInitialCarouselLoad &&
    initCarouselMethods[0].payGroupActionType !== ActionTypes.NestedList
  ) {
    return initCarouselMethods[0];
  }

  return undefined;
};

export const formWithdrawalGroups = (
  payload?: InitSuccess,
  userAgent?: string,
  appChannel?: string,
  initCurrency?: string,
  args?: { isOnRamp?: boolean; hasOnRampCurrency?: boolean },
): UIPayout => {
  if (!payload) {
    return {
      tax: undefined,
      methodsTypes: [],
      isReady: false,
      canAutoRedirect: true,
      isRequestPending: false,
      submitPaymentReady: false,
      currencyId: undefined,
      currencyAlpha3: undefined,
      widgetSettings: {
        country_filtering_enabled: false,
      },
      methods: [],
      lastDeclineInfo: null,
      carouselMethodList: [],
      methodToRedirect: undefined,
      viewData: [],
      merchantUrl: undefined,
      merchantSuccessUrl: undefined,
      merchantFailUrl: undefined,
      merchantProcessingUrl: undefined,
      merchantRedirectType: undefined,
      userBalance: 0,
      growthbookFeatures: undefined,
      authToken: '',
      merchantApiKey: '',
      paygroupCategories: undefined,
      shouldSkipInitFeature: false,
      selectedCurrency: null,
      defaultFiatCurrency: null,
      walletCurrencies: [],
      wagerFeePercent: undefined,
      wagerAmount: undefined,
      meshMethods: [],
      userCurrency: null,
      userBlocked: false,
    };
  }

  const { isOnRamp = false } = args || {};

  const {
    view_data,
    payment_groups = null,
    payout_groups = null,
    cashier_settings,
    tax = {},
    cashier_features = [],
    merchant_url,
    merchant_success_url,
    merchant_fail_url,
    merchant_processing_url,
    merchant_redirect_type,
    user_balance = 0,
    user_blocked,
    user_currency = 0,
    auth_token = null,
    merchant_api_key = null,
    last_decline_info = null,
    merchant_paygroup_category,
    wallet_currencies = [],
    wager_fee_percent,
    default_fiat_currency,
  } = payload;
  const billingFeatures = (cashier_features || []).filter((f) => f.startsWith('billing'));
  const isWagerAmountExist = view_data?.some((item) => item.params?.wager_amount);
  const wagerAmount = view_data?.find((item) => item.params?.wager_amount)?.params?.wager_amount;
  const initialTheme = getInitialTheme();
  const receivedGroups = payment_groups || payout_groups;
  const groups = Array.isArray(receivedGroups) ? receivedGroups : [];
  const preparedViewData = (groups?.length ? view_data : [noMethodsError, ...(view_data || [])]) as ViewDataType;
  const isLimitReached = getIsLimitReached(preparedViewData);
  const generalSettings: GeneralInfo = {
    isLimitReached,
    generalViewData: preparedViewData,
    // https://gr8-tech.atlassian.net/browse/BILL-14691
    // https://gr8-tech.atlassian.net/browse/BILL-20414
    // https://gr8-tech.atlassian.net/browse/BILL-21808
    disabledByWager: !!(isWagerAmountExist && WAGERING_BRANDS.includes(initialTheme)),
  };
  const methods = [...groups]
    .filter((group) => !isOnRamp || group.pay_method_type === 'onRamp')
    .sort(sortByArrangement)
    .map(formWithdrawalMethod(generalSettings, merchant_paygroup_category));
  const methodsByPayType = filterMethodsByPayType(methods as UIPaymentGroups[], userAgent, appChannel);
  const currencyId = methodsByPayType && methodsByPayType[0]?.currencyId;
  const currencyAlpha3 = user_currency || (methodsByPayType && methodsByPayType[0]?.currencyAlpha3);
  const methodsTypes = getMethodsTypes(methodsByPayType);
  const getMethodForAutoRedirectCallback = (initCarouselMethods: UIPayoutGroups[]) =>
    getMethodForAutoRedirectWithdrawal({
      methods: methodsByPayType,
      initCarouselMethods,
    });
  const { methodToRedirect, carouselMethodList } = getCarouselMethodsAndMethodToRedirect<UIPayoutGroups>(
    methodsByPayType,
    getMethodForAutoRedirectCallback,
  );
  const selectedCurrency = getSelectedCurrency({
    walletCurrencies: wallet_currencies,
    userCurrency: user_currency || null,
    groups: methods,
    initCurrency,
  });
  const isCryptoMultiCashier = cashier_settings.multi_currency_cashier && selectedCurrency?.isFiat === false;
  const shouldSkipInitFeature = methods.length !== 0 && !user_blocked && isCryptoMultiCashier;
  const formatedCurrencies = wallet_currencies.map(mapToCurrencyBase);

  return {
    tax,
    methodsTypes,
    isReady: true,
    canAutoRedirect: true,
    isRequestPending: false,
    submitPaymentReady: false,
    currencyId,
    currencyAlpha3,
    widgetSettings: cashier_settings,
    methods: methodsByPayType,
    carouselMethodList,
    methodToRedirect,
    viewData: formNotificationData({
      viewData: preparedViewData as ViewDataType,
      lastDeclineInfo: last_decline_info,
      isGeneral: true,
    }),
    merchantUrl: merchant_url,
    lastDeclineInfo: last_decline_info,
    merchantSuccessUrl: merchant_success_url,
    merchantFailUrl: merchant_fail_url,
    merchantProcessingUrl: merchant_processing_url,
    merchantRedirectType: merchant_redirect_type,
    userBalance: user_balance,
    userCurrency: user_currency || null,
    growthbookFeatures: billingFeatures,
    authToken: auth_token || '',
    merchantApiKey: merchant_api_key || '',
    paygroupCategories: merchant_paygroup_category,
    walletCurrencies: formatedCurrencies || [],
    shouldSkipInitFeature,
    selectedCurrency,
    wagerFeePercent: wager_fee_percent,
    wagerAmount,
    userBlocked: user_blocked || false,
    defaultFiatCurrency: default_fiat_currency || null,
    meshMethods: [],
  };
};

export const formWithdrawalRequiredFields = (
  parameters: PayHubCashierContractsInitParamsResponse[] | null | undefined,
): Parameter[] | [] => {
  if (!parameters || parameters.length === 0) {
    return [];
  }

  return parameters.map(
    ({
      name,
      type,
      extended_type,
      data_type,
      regexp,
      label,
      place_holder,
      place_holder_empty_list,
      place_holder_empty_list_error,
      is_required,
      is_otp_requisites,
      options,
      user_requisite,
      user_requisite_type_id,
      verify_new_payout_account,
      validation_msg_key,
    }: PayHubCashierContractsInitParamsResponse): Parameter => {
      const formattedOptions =
        options && options.length
          ? options.map((option) => ({
              ...option,
              selected: option.is_selected || false,
              name: option.label || '',
              value: option.value || '',
            }))
          : undefined;

      return {
        name: name || '',
        key: name || JSON.stringify(label),
        type: type || PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamType.Input,
        extendedType: extended_type,
        dataType: data_type || PayHubAdminPanelDomainEnumsParameterDataType.String,
        regexp: regexp || undefined,
        label,
        placeHolder: place_holder,
        placeHolderEmptyList: place_holder_empty_list,
        placeHolderEmptyListError: place_holder_empty_list_error,
        isRequired: is_required,
        isOtpRequisites: is_otp_requisites,
        options: formattedOptions,
        userRequisite: user_requisite || null,
        verifyNewPayoutAccount: verify_new_payout_account,
        userRequisiteTypeId: user_requisite_type_id || null,
        validationMsgKey: validation_msg_key,
      };
    },
  );
};

export const formWithdrawalMethodInfo = (
  response: PayHubWidgetContractsPayoutsSelectPayoutGroupResponse,
  requiredFieldsFormatter: (params: PayHubCashierContractsInitParamsResponse[] | null | undefined) => Parameter[],
  payGroupId: number,
): PayoutProcessMethodInfo => {
  const {
    currency = {},
    limits = {},
    amount_blocks,
    parameters,
    view_data,
    processing_currency,
    show_processing_currency,
    amount_reg_exp_fe,
    auto_submit,
    payout_account_verification_blocked,
    payout_account_verification_blocked_until = '',
    support_url,
    trusted_contacts,
    allow_new_payout_account,
    merchant_user_fee,
    processing_currency_rate,
    info,
    processing_time_translation_key,
    fee,
    is_routable,
    workflow_type,
    amount_can_be_changed,
    possible_changes_amount,
  } = response;
  const { currency_id, alpha3 } = currency;
  const { min_amount, max_amount, max_recurring_amount } = limits;

  const limitsObject = {
    min: min_amount || 0,
    max: max_amount || undefined,
    maxRecurrentAmount: max_recurring_amount || undefined,
  };
  const requiredFields = requiredFieldsFormatter(parameters);
  const { additionalViewData, viewData } = getViewData(view_data);

  return {
    showProcessingTime: show_processing_currency as boolean,
    currencyAlpha3: alpha3 || DEFAULT_EMPTY_CURRENCY_ALPHA3,
    limits: limitsObject,
    currencyId: currency_id || 0,
    requiredFields,
    additionalViewData,
    // TODO: double check
    viewData: formNotificationData({
      viewData,
      payGroupId,
    }),
    processingCurrency: processing_currency,
    amountBlocks: amount_blocks,
    amountRegExpFe: amount_reg_exp_fe || undefined,
    autoSubmit: auto_submit,
    payoutAccountVerifBlocked: payout_account_verification_blocked,
    payoutAccountVerifBlockedUntil: payout_account_verification_blocked_until,
    supportUrl: support_url,
    trustedContacts: trusted_contacts,
    allowNewPayoutAccount: allow_new_payout_account,
    merchantUserFee: merchant_user_fee,
    processingCurrencyRate: processing_currency_rate || undefined,
    info: info || undefined,
    processingTimeTranslationKey: processing_time_translation_key || undefined,
    fee: fee || undefined,
    isRoutable: is_routable,
    workFlowType: workflow_type,
    amountCanBeChanged: amount_can_be_changed || false,
    possibleChangesAmount: possible_changes_amount ? Number(possible_changes_amount) : 0,
  };
};
